import { Page } from "@/app/shared/components/page";
import { PageHeader } from "@groot/shell/components/layout";
import { Button } from "@groot/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@groot/ui/dialog";
import { LoadingSpinner } from "@groot/ui/loading-spinner";
import { toast } from "@/lib/toast";
import { apiClient } from "@/services/api";
import type { Payslip } from "@/types";
import dayjs from "dayjs";
import { ArrowLeft, Download, Maximize2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

export default function PayslipView() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [payslip, setPayslip] = useState<Payslip | null>(null);
  const [loading, setLoading] = useState(true);
  const [isFullScreen, setIsFullScreen] = useState(false);

  useEffect(() => {
    if (id) {
      loadPayslip(Number.parseInt(id));
    }
  }, [id]);

  const loadPayslip = async (payslipId: number) => {
    try {
      setLoading(true);
      const data = await apiClient.getPayslip(payslipId, true);
      setPayslip(data);
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load payslip",
      });
      console.error(err);
      navigate("/payslips");
    } finally {
      setLoading(false);
    }
  };

  const formatMonth = (month: string) => {
    return dayjs(month, "YYYY-MM").format("MMMM YYYY");
  };

  if (loading) {
    return (
      <Page>
        <LoadingSpinner size="lg" className="py-20" />
      </Page>
    );
  }

  if (!payslip) {
    return null;
  }

  return (
    <Page>
      {/* Header */}
      <PageHeader
        title={`${payslip.company} - ${formatMonth(payslip.month)}`}
        description="Payslip details and PDF view"
        className="mb-8"
        actions={
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => navigate("/payslips")}>
              <ArrowLeft className="size-4" />
              Back
            </Button>
            <Button asChild>
              <a
                href={apiClient.getPayslipPdfUrl(payslip.id)}
                download={payslip.fileName}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Download className="size-4" />
                Download
              </a>
            </Button>
          </div>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Details Section */}
        <div className="space-y-6">
          <div className="border border-border rounded-lg overflow-hidden">
            <div className="border-b border-border bg-muted px-6 py-4">
              <h2 className="text-base font-medium text-foreground">Details</h2>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <p className="text-sm text-muted-foreground">Company</p>
                <p className="font-medium">{payslip.company}</p>
              </div>

              <div>
                <p className="text-sm text-muted-foreground">Month</p>
                <p className="font-medium">{formatMonth(payslip.month)}</p>
              </div>

              <div>
                <p className="text-sm text-muted-foreground">File Name</p>
                <p className="font-medium">{payslip.fileName}</p>
              </div>

              {payslip.grossEarnings && (
                <div>
                  <p className="text-sm text-muted-foreground">Gross Earnings</p>
                  <p className="font-medium text-success">
                    ₹{payslip.grossEarnings.toLocaleString()}
                  </p>
                </div>
              )}

              {payslip.grossDeductions && (
                <div>
                  <p className="text-sm text-muted-foreground">Gross Deductions</p>
                  <p className="font-medium text-destructive">
                    ₹{payslip.grossDeductions.toLocaleString()}
                  </p>
                </div>
              )}

              {payslip.netPay && (
                <div className="pt-4 border-t border-border">
                  <p className="text-sm text-muted-foreground">Net Pay</p>
                  <p className="font-medium text-foreground text-xl">
                    ₹{payslip.netPay.toLocaleString()}
                  </p>
                </div>
              )}

              {payslip.notes && (
                <div className="pt-4 border-t border-border">
                  <p className="text-sm text-muted-foreground">Notes</p>
                  <p className="text-sm">{payslip.notes}</p>
                </div>
              )}
            </div>
          </div>

          {/* Earnings */}
          {payslip.earnings && payslip.earnings.length > 0 && (
            <div className="border border-border rounded-lg overflow-hidden">
              <div className="border-b border-border bg-muted px-6 py-4">
                <h2 className="text-base font-medium text-foreground">Earnings</h2>
              </div>
              <div className="p-6">
                <div className="space-y-2">
                  {payslip.earnings.map((earning) => (
                    <div
                      key={`${earning.description}-${earning.amount}`}
                      className="flex justify-between text-sm"
                    >
                      <span className="text-muted-foreground">{earning.description}</span>
                      <span className="font-medium">
                        ₹{Number(earning.amount).toLocaleString()}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Deductions */}
          {payslip.deductions && payslip.deductions.length > 0 && (
            <div className="border border-border rounded-lg overflow-hidden">
              <div className="border-b border-border bg-muted px-6 py-4">
                <h2 className="text-base font-medium text-foreground">Deductions</h2>
              </div>
              <div className="p-6">
                <div className="space-y-2">
                  {payslip.deductions.map((deduction) => (
                    <div
                      key={`${deduction.description}-${deduction.amount}`}
                      className="flex justify-between text-sm"
                    >
                      <span className="text-muted-foreground">{deduction.description}</span>
                      <span className="font-medium">
                        ₹{Number(deduction.amount).toLocaleString()}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* PDF Viewer Section */}
        <div>
          <div className="border border-border rounded-lg overflow-hidden">
            <div className="border-b border-border bg-muted px-6 py-4 flex items-center justify-between">
              <h2 className="text-base font-medium text-foreground">PDF Document</h2>
              <Button variant="outline" size="sm" onClick={() => setIsFullScreen(true)}>
                <Maximize2 className="size-4 mr-2" />
                Full Screen
              </Button>
            </div>
            <div className="p-6">
              <div className="w-full aspect-[8.5/11] bg-muted rounded-lg overflow-hidden">
                <iframe
                  src={apiClient.getPayslipPdfUrl(payslip.id)}
                  className="w-full h-full border-0"
                  title="Payslip PDF"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Full Screen PDF Dialog */}
      <Dialog open={isFullScreen} onOpenChange={setIsFullScreen}>
        <DialogContent className="max-w-[95vw] max-h-[95vh] h-[95vh] p-0 flex flex-col">
          <DialogHeader className="px-6 pt-6 pb-4 shrink-0">
            <DialogTitle>
              {payslip.company} - {formatMonth(payslip.month)}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 px-6 pb-6 min-h-0">
            <div className="w-full h-full bg-muted rounded-lg overflow-hidden">
              <iframe
                src={apiClient.getPayslipPdfUrl(payslip.id)}
                className="w-full h-full border-0"
                title="Payslip PDF - Full Screen"
              />
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Page>
  );
}
